import React, { useState, useMemo, useEffect } from "react";
import Autosuggest from "react-autosuggest";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import TextField from "@material-ui/core/TextField";
import Paper from "@material-ui/core/Paper";
import MenuItem from "@material-ui/core/MenuItem";
import Popper from "@material-ui/core/Popper";
import { Field, change } from "redux-form";
import { makeStyles } from "@material-ui/core/styles";
import { fetchSponsorsAutocomplete } from "../redux/actions";
import { sponsorsAutocompleteSelector } from "../redux/selectors";
import { useConstant } from "../utilities/useConstant";
import { debounceMilliseconds } from "../utilities/constants";
var debounce = require("lodash/debounce");

const useASStyles = makeStyles((theme) => ({
  root: {
    //height: 75,
    color: "white",
    flexGrow: 1,
  },
  container: {
    position: "relative",
  },
  suggestionsContainerOpen: {
    position: "absolute",
    zIndex: 1,
    marginTop: theme.spacing(1),
    left: 0,
    right: 0,
  },
  suggestion: {
    display: "block",
  },
  suggestionsList: {
    margin: 0,
    padding: 0,
    listStyleType: "none",
  },
  optionStyle: {
    overflowY: "auto",
    "& ul li .MuiMenuItem-root": {
      whiteSpace: "normal",
    },
  },
}));

const styles = {
  input: {
    color: "white",
    fontSize: "1rem",
    backgroundColor: "rgb(36, 48, 65)",
  },

  root: {
    color: "white",
  },
  focused: {
    color: "white",
    backgroundColor: "rgb(36, 48, 65)",
    "&$focused": {
      color: "white",
      backgroundColor: "rgb(36, 48, 65)",
    },
  },
};

const useLabelStyles = makeStyles({
  root: {
    color: "white",
    // backgroundColor: 'rgb(36, 48, 65)',
    marginLeft: "-3px",
  },
  focused: {
    color: "white",
    // backgroundColor: 'rgb(36, 48, 65)',
    marginLeft: "-3px",
  },
});

const useInputStyles = makeStyles({
  root: {
    color: "white",
    backgroundColor: "rgb(36, 48, 65)",
  },
  focused: {
    color: "white",
    backgroundColor: "rgb(36, 48, 65)",
    "&$focused": {
      color: "white",
      backgroundColor: "rgb(36, 48, 65)",
    },
  },
});

export const SponsorSelectorComponent = ({
  label,
  meta: { initial, form, touched, invalid, error },
  input,
  noMatchText,
  defaultLabel,
}) => {
  const sponsors = useSelector(sponsorsAutocompleteSelector, shallowEqual);
  const dispatch = useDispatch();
  var labelStyles = useLabelStyles();
  var inputStyles = useInputStyles();
  const [noMatch, setNoMatch] = useState(true);
  const [matchSelected, setMatchSelected] = useState(false);
  const [stateSuggestions, setSuggestions] = useState(sponsors);
  const classes = useASStyles();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [state, setState] = React.useState({
    label: "",
    value: initial,
  });

  const setSetState = (newState) => {
    setState(newState);
  };

  useEffect(() => {
    if (defaultLabel && initial) {
      setSetState({ label: defaultLabel, value: initial });
      setNoMatch(false);
      setMatchSelected(true);
    } else {
      setSetState({ label: "", value: null });
      setNoMatch(true);
      setMatchSelected(false);
    }
  }, [!!defaultLabel, !!initial]);

  const handleSuggestionsFetchRequested = ({ value }) => {
    dispatch(fetchSponsorsAutocomplete(value));
  };

  const onSuggestionRequested = useConstant(() =>
    debounce(handleSuggestionsFetchRequested, debounceMilliseconds, {
      leading: true,
    }),
  );

  function renderSuggestion(suggestion, { query, isHighlighted }) {
    return (
      <MenuItem selected={isHighlighted} component="div">
        <div>{suggestion.name}</div>
      </MenuItem>
    );
  }

  function renderInputComponent(inputProps) {
    const { classes, inputRef = () => {}, ref, ...other } = inputProps;

    return (
      <TextField
        fullWidth
        autoComplete={false}
        InputLabelProps={{
          classes: {
            root: labelStyles.root,
            focused: labelStyles.focused,
            shrink: labelStyles.focused,
          },
        }}
        error={touched && invalid}
        helperText={touched && error}
        classes={{
          root: inputStyles.root,
          error: inputStyles.error,
        }}
        style={styles}
        InputProps={{
          ...inputProps,
          inputRef: (node) => {
            ref(node);
            inputRef(node);
          },
        }}
        inputProps={{ autoComplete: "dont" }}
        shrink="true"
        variant="filled"
        {...other}
      />
    );
  }

  const handleSuggestionsClearRequested = () => {
    setSuggestions([]);
  };

  const onSuggestionSelected = (
    event,
    { suggestion, suggestionValue, suggestionIndex, sectionIndex, method },
  ) => {
    if (suggestion && suggestion.id > -1) {
      dispatch(change(form, input.name, suggestion.id));
      setNoMatch(false);
      setMatchSelected(true);
    } else {
      setSetState({
        ...state,
        value: null,
        label: "",
      });
      setNoMatch(true);
      dispatch(change(form, input.name, ""));
    }
  };

  function getSuggestionValue(suggestion) {
    return suggestion;
  }

  const autosuggestProps = {
    renderInputComponent,
    onSuggestionsClearRequested: handleSuggestionsClearRequested,
    onSuggestionSelected: onSuggestionSelected,
    getSuggestionValue,
    renderSuggestion,
  };

  const handleChange = (event, { newValue }) => {
    if (newValue && newValue.id) {
      dispatch(change(form, input.name, newValue.id));
      setNoMatch(false);
      setMatchSelected(true);
    } else {
      dispatch(change(form, input.name, null));
      setNoMatch(true);
      setMatchSelected(false);
    }
    setSetState({
      ...state,
      value: newValue,
      label: newValue && newValue.name ? newValue.name : newValue,
    });
  };

  return (
    <div className={classes.root}>
      <Autosuggest
        {...autosuggestProps}
        suggestions={sponsors}
        onSuggestionsFetchRequested={onSuggestionRequested}
        inputProps={{
          classes: {
            root: classes.root,
          },
          id: "react-autosuggest-popper",
          label: label,
          value: state.label,
          onChange: handleChange,

          onBlur: (e, x) => {
            setAnchorEl(null);
            //input.onChange(e.target.value)
            if (noMatch || !matchSelected) {
              setSetState({
                ...state,
                value: "",
                label: "",
              });
              dispatch(change(form, input.name, ""));
            }
          },
          inputRef: (node) => {
            setAnchorEl(node);
          },
        }}
        theme={{
          suggestionsList: classes.suggestionsList,
          suggestion: classes.suggestion,
        }}
        renderSuggestionsContainer={(options) => (
          <Popper
            anchorEl={anchorEl}
            open={Boolean(options.children)}
            style={{ zIndex: 1300 }}
            placement="bottom-start"
            modifiers={{
              flip: {
                enabled: false,
              },
              preventOverflow: {
                enabled: true,
                boundariesElement: "viewPort",
              },
            }}
          >
            <Paper
              square
              {...options.containerProps}
              className={classes.optionStyle}
              style={{
                maxHeight: anchorEl
                  ? window.innerHeight -
                    anchorEl.parentElement.getBoundingClientRect().bottom
                  : undefined,
                width: anchorEl
                  ? anchorEl.parentElement.clientWidth
                  : undefined,
                marginLeft: anchorEl
                  ? -(anchorEl.parentElement.clientWidth - anchorEl.clientWidth)
                  : undefined,
              }}
            >
              {options.children}
            </Paper>
          </Popper>
        )}
      />
    </div>
  );
};

export default ({
  label,
  name,
  validators,
  suggestions,
  maxLength = 255,
  noMatchText = "No Matches Found",
  protocolId,
  ...other
}) => {
  return (
    <Field
      name={name}
      component={SponsorSelectorComponent}
      label={label}
      maxLength={maxLength}
      fullWidth
      validate={validators}
      noMatchText={noMatchText}
      protocolId={protocolId}
      {...other}
    />
  );
};
